import { inject, Injectable } from '@angular/core';
import { asapScheduler, firstValueFrom, observeOn, take } from 'rxjs';

import { AuthService } from '@blaze-shared/auth-data-access';
import { ElementLink } from '@blaze-shared/elements-domain';
import { NotificationService } from '@blaze-shared/platform-data-access';

import { ElementsFacade } from '../+state/elements/elements.facade';

import { LayoutLoadingService } from './layout-loading.service';
import { ProjectNavigateService } from './project-navigate.service';

@Injectable({ providedIn: 'root' })
export class ElementLinkService {
  private readonly authService = inject(AuthService);
  private readonly elementsFacade = inject(ElementsFacade);
  private readonly layoutLoadingService = inject(LayoutLoadingService);
  private readonly notificationService = inject(NotificationService);
  private readonly projectNavigate = inject(ProjectNavigateService);

  async handleLinkTrigger(link: ElementLink): Promise<void> {
    const layoutEntities = await firstValueFrom(this.elementsFacade.layoutEntities$);

    switch (link.action) {
      case 'GoToPage': {
        const pageId = link.navigatePageId;
        try {
          if (!pageId) {
            throw new Error('pageId is not defined');
          }
          if (layoutEntities?.[pageId]) {
            this.projectNavigate.navigateToPage({ targetPageId: pageId });
          } else {
            this.layoutLoadingService
              .loadAndWait(pageId)
              .pipe(take(1), observeOn(asapScheduler))
              .subscribe(() => {
                this.projectNavigate.navigateToPage({ targetPageId: pageId });
              });
          }
        } catch {
          this.notificationService.error('The clicked item is missing the page param', true);
        }
        break;
      }
      case 'Logout':
        this.authService.signOut(true);
        break;
      case 'Url':
        if (!link.navigateUrl) {
          this.notificationService.error(
            'The clicked item is missing a valid website address',
            true,
          );
        } else {
          window.open(link.navigateUrl, '_blank', 'noreferrer noopener');
        }
        break;
      default:
        break;
    }
  }
}
